// src/scenes/meal-entry/MealCountsMonthly.jsx
import React, { useState, useEffect } from "react";
import {
    Box,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    Button,
    Typography,
    useTheme,
    CircularProgress,
    Modal
} from "@mui/material";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

const blankCounts = {
    BR_Free: 0, BR_Reduced: 0, BR_Paid: 0,
    LN_Free: 0, LN_Reduced: 0, LN_Paid: 0,
    SP_Free: 0, SP_Reduced: 0, SP_Paid: 0,
    SN_Free: 0, SN_Reduced: 0, SN_Paid: 0,
};
const mealPrefixMap = { BR: "Breakfast", LN: "Lunch", SP: "Supper", SN: "Snack" };

export default function MealCountsEntryMonthly() {
    const theme = useTheme();
    const {
        palette: { divider, action, background, text, primary, success, warning, error: errColor, info }
    } = theme;
    const { allocations, isSchoolUser } = useAuth();
    const allowedIds = allocations.map(a => a.SchoolId);

    // global selectors, default to 8 / 57
    const [globalCategory, setGlobalCategory] = useState(8);
    const [globalSubcategory, setGlobalSubcategory] = useState(57);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    // rows & lookups
    const [rows, setRows] = useState([]);
    const [schools, setSchools] = useState([]);
    const [rates, setRates] = useState({});
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState("");
    const [error, setError] = useState("");

    // progress
    const [progressOpen, setProgressOpen] = useState(false);
    const [progressMessage, setProgressMessage] = useState("");

    // load lookups
    useEffect(() => {
        Promise.all([
            axios.get("https://figazuresql.swancreate.com/api/swa/SchoolMst"),
            axios.get("https://figazuresql.swancreate.com/api/swa/finance_categories"),
            axios.get("https://figazuresql.swancreate.com/api/swa/finance_subcategories"),
            axios.get("https://figazuresql.swancreate.com/api/swa/MealReimbursementRates"),
        ])
            .then(([schRes, catRes, subRes, rateRes]) => {
                let schList = schRes.data
                    .filter(s => s.SchoolId && s.SchoolName)
                    .map(s => ({ id: s.SchoolId, name: s.SchoolName }))
                    .sort((a, b) => a.name.localeCompare(b.name));
                if (isSchoolUser) {
                    schList = schList.filter(s => allowedIds.includes(s.id));
                }
                setSchools(schList);

                setCategories(catRes.data.filter(c => c.Type === "Income"));
                setSubcategories(subRes.data);

                const grouped = {};
                rateRes.data.forEach(r => {
                    if (!grouped[r.MealService]) grouped[r.MealService] = {};
                    grouped[r.MealService][r.PaymentStatus] = parseFloat(r.Rate);
                });
                setRates(grouped);
            })
            .catch(err => {
                console.error(err);
                setFetchError("Lookup load failed");
            })
            .finally(() => {
                setLoading(false);
            });
    }, [allocations, isSchoolUser]);

    // once schools load, initialize first row
    useEffect(() => {
        if (schools.length) {
            setRows([{
                ...blankCounts,
                tempId: crypto.randomUUID(),
                siteId: schools[0].id,
                siteName: schools[0].name,
                date: "",
            }]);
        }
    }, [schools]);

    const handleCellChange = (idx, field, value) => {
        setRows(rs => rs.map((r, i) => {
            if (i !== idx) return r;
            if (field === "siteId") {
                const match = schools.find(s => s.id === value) || {};
                return { ...r, siteId: value, siteName: match.name || "" };
            }
            return { ...r, [field]: field === "date" ? value : Number(value) || 0 };
        }));
    };

    const mealTotalMoney = (r, prefix) => {
        const svc = rates[mealPrefixMap[prefix]] || {};
        return (
            r[`${prefix}_Free`] * (svc.Free || 0) +
            r[`${prefix}_Reduced`] * (svc.Reduced || 0) +
            r[`${prefix}_Paid`] * (svc.Paid || 0)
        );
    };
    const rowTotalMoney = r =>
        Object.keys(mealPrefixMap).reduce((sum, p) => sum + mealTotalMoney(r, p), 0);

    const addRow = () => {
        const first = schools[0] || { id: "", name: "" };
        setRows(rs => [
            ...rs,
            {
                ...blankCounts,
                tempId: crypto.randomUUID(),
                siteId: first.id,
                siteName: first.name,
                date: "",
            }
        ]);
    };
    const removeRow = idx => setRows(rs => rs.filter((_, i) => i !== idx));

    const handleSubmit = async () => {
        if (!globalCategory) { setError("Select a Category."); return; }
        if (!globalSubcategory) { setError("Select a Subcategory."); return; }
        for (let r of rows) {
            if (!r.siteId || !r.date) {
                setError("Each row needs Site and Date."); return;
            }
        }
        setError(""); setProgressOpen(true);

        try {
            // meal counts
            for (let i = 0; i < rows.length; i++) {
                const r = rows[i];
                setProgressMessage(`Saving meal counts for ${r.siteName}�`);
                await axios.post(
                    "https://figazuresql.swancreate.com/api/swa/FINANCE_MealCounts",
                    {
                        MonthDate: r.date,
                        Date: r.date,
                        SchoolId: r.siteId,
                        SchoolName: r.siteName,
                        BR_Free: r.BR_Free,
                        BR_Reduced: r.BR_Reduced,
                        BR_Paid: r.BR_Paid,
                        LN_Free: r.LN_Free,
                        LN_Reduced: r.LN_Reduced,
                        LN_Paid: r.LN_Paid,
                        SP_Free: r.SP_Free,
                        SP_Reduced: r.SP_Reduced,
                        SP_Paid: r.SP_Paid,
                        SN_Free: r.SN_Free,
                        SN_Reduced: r.SN_Reduced,
                        SN_Paid: r.SN_Paid,

                        // ** now include these totals explicitly **
                        BR_Total: mealTotalMoney(r, "BR"),
                        LN_Total: mealTotalMoney(r, "LN"),
                        SP_Total: mealTotalMoney(r, "SP"),
                        SN_Total: mealTotalMoney(r, "SN"),
                        Summary_Total: rowTotalMoney(r),

                        // you can add any other required default fields here
                    }
                );
            }

            // incomes
            for (let i = 0; i < rows.length; i++) {
                const r = rows[i];
                setProgressMessage(`Saving income for ${r.siteName}�`);
                const catName = categories.find(c => c.CategoryID === globalCategory)?.CategoryName || "";
                const subName = subcategories.find(sc => sc.SubcategoryID === globalSubcategory)?.SubcategoryName || "";
                await axios.post(
                    "https://figazuresql.swancreate.com/api/swa/FINANCE_Incomes",
                    {
                        MonthDate: r.date,
                        IncomeDate: r.date,
                        SchoolId: r.siteId,
                        SiteName: r.siteName,
                        IncomeCategoryID: globalCategory,
                        IncomeCategory: catName,
                        IncomeSubCategoryID: globalSubcategory,
                        IncomeSubcategory: subName,
                        Amount: rowTotalMoney(r),
                        Description: "",
                    }
                );
            }

            // reset
            setRows([{
                ...blankCounts,
                tempId: crypto.randomUUID(),
                siteId: schools[0].id,
                siteName: schools[0].name,
                date: "",
            }]);
        } catch (e) {
            console.error(e);
            setError("Save failed");
        } finally {
            setProgressOpen(false);
        }
    };

    if (loading) return <CircularProgress />;
    if (fetchError) return <Typography color="error">{fetchError}</Typography>;

    const evenBg = theme.palette.mode === "light" ? action.hover : primary[600];
    const oddBg = theme.palette.mode === "light" ? background.paper : primary[700];

    return (
        <Box p={3}>
            <Typography variant="h4" gutterBottom>
                FIG Monthly Meal Counts by Site
            </Typography>
            <Box display="flex" gap={2} mb={3}>
                <FormControl size="small">
                    <InputLabel>Category</InputLabel>
                    <Select
                        value={globalCategory}
                        onChange={e => setGlobalCategory(e.target.value)}
                        label="Category"
                    >
                        {categories.map(c => (
                            <MenuItem key={c.CategoryID} value={c.CategoryID}>
                                {c.CategoryName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small">
                    <InputLabel>Subcategory</InputLabel>
                    <Select
                        value={globalSubcategory}
                        onChange={e => setGlobalSubcategory(e.target.value)}
                        label="Subcategory"
                    >
                        {subcategories
                            .filter(sc => sc.CategoryID === globalCategory)
                            .map(sc => (
                                <MenuItem key={sc.SubcategoryID} value={sc.SubcategoryID}>
                                    {sc.SubcategoryName}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
            </Box>

            <Table size="small" sx={{ border: `1px solid ${divider}` }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ backgroundColor: primary.main, color: "#fff" }}>Site</TableCell>
                        <TableCell sx={{ backgroundColor: primary.main, color: "#fff" }}>Date</TableCell>
                        {Object.entries(mealPrefixMap).map(([p, m]) => (
                            <React.Fragment key={p}>
                                <TableCell
                                    align="center"
                                    sx={{
                                        backgroundColor:
                                            m === "Breakfast" ? success.main :
                                                m === "Lunch" ? info.main :
                                                    m === "Supper" ? warning.main :
                                                        errColor.main,
                                        color: "#fff"
                                    }}
                                >
                                    {m}<br />
                                    <Typography component="span" variant="caption">F&nbsp;R&nbsp;P</Typography>
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        backgroundColor:
                                            m === "Breakfast" ? success.main :
                                                m === "Lunch" ? info.main :
                                                    m === "Supper" ? warning.main :
                                                        errColor.main,
                                        color: "#fff"
                                    }}
                                >
                                    $
                                </TableCell>
                            </React.Fragment>
                        ))}
                        <TableCell sx={{ backgroundColor: primary.main, color: "#fff" }}>Total $</TableCell>
                        <TableCell sx={{ backgroundColor: primary.main }} />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((r, i) => (
                        <TableRow key={r.tempId} sx={{ backgroundColor: i % 2 ? evenBg : oddBg }}>
                            <TableCell>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={r.siteId}
                                        onChange={e => handleCellChange(i, "siteId", e.target.value)}
                                        size="small"
                                    >
                                        {schools.map(s => (
                                            <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </TableCell>
                            <TableCell>
                                <TextField
                                    type="date"
                                    size="small"
                                    value={r.date}
                                    onChange={e => handleCellChange(i, "date", e.target.value)}
                                    fullWidth
                                />
                            </TableCell>
                            {Object.keys(mealPrefixMap).map(prefix => (
                                <React.Fragment key={prefix}>
                                    <TableCell>
                                        <Box display="flex" gap={1}>
                                            {["Free", "Reduced", "Paid"].map(status => (
                                                <TextField
                                                    key={status}
                                                    type="number"
                                                    value={r[`${prefix}_${status}`]}
                                                    onChange={e => handleCellChange(i, `${prefix}_${status}`, e.target.value)}
                                                    size="small"
                                                    sx={{
                                                        width: 60,
                                                        "& input[type=number]": { MozAppearance: "textfield" },
                                                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                                            WebkitAppearance: "none",
                                                            margin: 0
                                                        }
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        {mealTotalMoney(r, prefix).toLocaleString(undefined, {
                                            style: "currency",
                                            currency: "USD"
                                        })}
                                    </TableCell>
                                </React.Fragment>
                            ))}
                            <TableCell>
                                {rowTotalMoney(r).toLocaleString(undefined, {
                                    style: "currency",
                                    currency: "USD"
                                })}
                            </TableCell>
                            <TableCell>
                                <IconButton onClick={() => removeRow(i)}>
                                    <RemoveCircleOutline />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow>
                        <TableCell colSpan={Object.keys(mealPrefixMap).length * 2 + 3} align="center">
                            <IconButton onClick={addRow}>
                                <AddCircleOutline /> Add Row
                            </IconButton>
                        </TableCell>
                        <TableCell />
                    </TableRow>
                </TableBody>
            </Table>

            {error && <Typography color="error" mt={2}>{error}</Typography>}

            <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                <Button variant="contained" onClick={handleSubmit}>Submit</Button>
                <Typography>
                    Summary Total:{" "}
                    <Typography component="span" color="text.secondary" fontWeight="bold">
                        {rows.reduce((sum, r) => sum + rowTotalMoney(r), 0).toLocaleString(undefined, {
                            style: "currency", currency: "USD"
                        })}
                    </Typography>
                </Typography>
            </Box>

            <Modal open={progressOpen} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Box sx={{ backgroundColor: background.paper, p: 3, borderRadius: 2, boxShadow: 24, textAlign: "center" }}>
                    <CircularProgress />
                    <Typography mt={2}>{progressMessage || "Processing�"}</Typography>
                </Box>
            </Modal>
        </Box>
    );
}
